import { sanitizeBalanceRules } from '../utils/available-balance-options';
import { DEFAULT_BALANCE_ENABLED } from '../configuration.constants';

/**
 * Setters owned by the Available Balance tab. All persistent state (the
 * threshold, double-dip cap, balance rules and enabled flags) lives in the
 * host component's `state.tabs.availableBalance`; every method below routes
 * through `this.setState(partial)`.
 */
class AvailableBalanceDelegate {
    setThreshold(threshold) {
        if (threshold === null || threshold === undefined || threshold === '') {
            this.setState({ threshold: null });
            return;
        }
        // Convert percentage to decimal (70 -> 0.7) and round to 4 decimal
        // places so values round-trip cleanly.
        const decimalValue = Math.round((threshold / 100) * 10000) / 10000;
        this.setState({ threshold: decimalValue });
    }

    setDoubleDipCap(cap) {
        if (cap === null || cap === undefined || cap === '') {
            this.setState({ doubleDipCap: null });
            return;
        }
        const decimalValue = Math.round((cap / 100) * 10000) / 10000;
        this.setState({ doubleDipCap: decimalValue });
    }

    setBalanceRules(balanceRules) {
        this.setState({ balance_rules: balanceRules });
    }

    setBalanceEnabled(balanceEnabled) {
        this.setState({
            balance_enabled: {
                userTenure: balanceEnabled?.userTenure ?? DEFAULT_BALANCE_ENABLED.userTenure,
                overdraft: balanceEnabled?.overdraft ?? DEFAULT_BALANCE_ENABLED.overdraft,
                ach: balanceEnabled?.ach ?? DEFAULT_BALANCE_ENABLED.ach,
                employment: balanceEnabled?.employment ?? DEFAULT_BALANCE_ENABLED.employment,
                balance: balanceEnabled?.balance ?? DEFAULT_BALANCE_ENABLED.balance,
                doubleDipCap: balanceEnabled?.doubleDipCap ?? DEFAULT_BALANCE_ENABLED.doubleDipCap
            }
        });
    }

    toggleBalanceRuleGroup(groupKey) {
        this.setState((prev) => ({
            balance_enabled: {
                ...prev.balance_enabled,
                [groupKey]: !prev.balance_enabled?.[groupKey]
            }
        }));
    }

    appendRule(stateKey, ruleType, rule) {
        this.setState((prev) => ({
            balance_rules: {
                ...prev.balance_rules,
                [stateKey]: [...(prev.balance_rules[stateKey] || []), rule]
            },
            balance_enabled: {
                ...prev.balance_enabled,
                [ruleType]: true
            }
        }));
    }

    removeBalanceRule(stateKey, ruleId) {
        this.setState((prev) => ({
            balance_rules: {
                ...prev.balance_rules,
                [stateKey]: (prev.balance_rules[stateKey] || []).filter((rule) => rule.id !== ruleId)
            }
        }));
    }

    updateBalanceRule(stateKey, ruleId, field, value) {
        this.setState((prev) => ({
            balance_rules: {
                ...prev.balance_rules,
                [stateKey]: (prev.balance_rules[stateKey] || []).map((rule) =>
                    rule.id === ruleId ? { ...rule, [field]: value } : rule
                )
            }
        }));
    }

    /**
     * Sanitizes balance rules against the given segmentation values.
     * Called by {@link SegmentationController.notifyAvailableBalance} with
     * the just-computed policy and enabled flags as explicit arguments,
     * so this update lands in the same React batch as the segmentation
     * change that triggered it.
     */
    sanitizeBalanceRulesWithSegmentation(segmentPolicy, segmentationEnabled) {
        this.setState((prev) => {
            const sanitized = sanitizeBalanceRules(
                prev.balance_rules,
                segmentPolicy,
                segmentationEnabled
            );
            if (JSON.stringify(sanitized) === JSON.stringify(prev.balance_rules)) return null;
            return { balance_rules: sanitized };
        });
    }
}

export default AvailableBalanceDelegate;
